import * as React from 'react'
import { API } from '../../services/api'
import { LoginResult } from '../../models/user_model'
import { inject, observer } from 'mobx-react'
import { UserStore } from '../../stores/UserModel'
import { useTranslation } from 'react-i18next'
import { History } from 'history'
import { USER_STORE, WORKSPACES_STORE } from '../../stores'
import { API_URL } from '../../constants'
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon, LockClosedIcon, FingerPrintIcon } from '@heroicons/react/24/outline'
import { isPasskeySupported, loginWithPasskey } from '../../services/passkeys'
import { getRandomArrayItem } from '../../utils/random'
import { WorkspacesStore } from '../../stores/WorkspaceStore'
import { TextInput } from './TextInput'
import { PinInput } from '../../components/PinInput/PinInput'
import { LogoWithText } from '../../components/LogoWithText'
import { isValidEmail } from '../../utils/email'

type LoginStep = 'credentials' | 'otp' | 'blocked'

interface LoginPageProps {
  [USER_STORE]?: UserStore
  [WORKSPACES_STORE]?: WorkspacesStore
  history: History
}

const TITLES = [
  'ZOBACZ', 'ODKRYJ', 'SPRAWDŹ', 'POCZUJ', 'KLIKNIJ',
  'NAPISZ', 'RUSZ', 'DOTKNIJ', 'POZNAJ',
]

const hasUppercase = (str: string): boolean => /[A-Z]/.test(str)
const hasNumber = (str: string): boolean => /[0-9]/.test(str)

export const LoginPage: React.FC<LoginPageProps> = observer(({
  [USER_STORE]: userStore,
  history,
  [WORKSPACES_STORE]: workspacesStore,
}) => {
  const { t } = useTranslation()
  const navigateToDashboard = React.useCallback(() => history.replace('/'), [history])
  const isLogged = userStore?.isLogged() ?? false

  React.useEffect(() => {
    if (isLogged) navigateToDashboard()
  }, [isLogged, navigateToDashboard])

  // Form state
  const [step, setStep] = React.useState<LoginStep>('credentials')
  const [displayedStep, setDisplayedStep] = React.useState<LoginStep>('credentials')
  const [animationClass, setAnimationClass] = React.useState('')

  const animateToStep = React.useCallback((nextStep: LoginStep) => {
    setAnimationClass('animate-form-out')
    setTimeout(() => {
      setStep(nextStep)
      setDisplayedStep(nextStep)
      setAnimationClass('animate-form-in')
    }, 250)
  }, [])
  const [values, setValues] = React.useState({
    loading: false,
    login: '',
    password: '',
    showPassword: false,
    error: '',
    loginError: '',
    passwordError: '',
  })

  // OTP state
  const [otpValues, setOtpValues] = React.useState({
    otp: '',
    error: '',
    loading: false,
    resendCooldown: 0,
  })

  // Unblock state
  const [unblockValues, setUnblockValues] = React.useState({
    code: '',
    error: '',
    loading: false,
    success: false,
  })

  // Resend cooldown timer
  React.useEffect(() => {
    if (otpValues.resendCooldown > 0) {
      const timer = setTimeout(() => {
        setOtpValues(prev => ({ ...prev, resendCooldown: prev.resendCooldown - 1 }))
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [otpValues.resendCooldown])

  const updateValue = (newValues: Partial<typeof values>) =>
    setValues(prev => ({ ...prev, ...newValues }))

  const handleChange = (prop: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    updateValue({ [prop]: e.target.value, error: '', loginError: '', passwordError: '' })

  const validateCredentials = (): boolean => {
    const { login, password } = values
    let isValid = true
    let loginError = ''
    let passwordError = ''

    if (!login) {
      loginError = t('loginPage.error.emailRequired')
      isValid = false
    } else if (!isValidEmail(login)) {
      loginError = t('loginPage.error.invalidEmail')
      isValid = false
    }

    if (!password) {
      passwordError = t('loginPage.error.passwordRequired')
      isValid = false
    } else {
      const errors: string[] = []
      if (!hasUppercase(password)) errors.push(t('loginPage.error.passwordNeedsUppercase'))
      if (!hasNumber(password)) errors.push(t('loginPage.error.passwordNeedsNumber'))
      if (errors.length > 0) {
        passwordError = errors.join('. ')
        isValid = false
      }
    }

    updateValue({ loginError, passwordError })
    return isValid
  }

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (values.loading) return
    if (!validateCredentials()) return

    updateValue({ loading: true, error: '' })

    const { login, password } = values
    const response = await API.getInstance().login(login, password)

    if (response.ok === true) {
      const result = response.data as LoginResult

      switch (result.status) {
        case 'logged_in':
          userStore.setUserData(result.user)
          await workspacesStore.fetch()
          navigateToDashboard()
          break
        case 'otp_required':
          userStore.setOtpRequired(result.session_id, result.delivery_method, login)
          updateValue({ loading: false })
          setOtpValues(prev => ({ ...prev, resendCooldown: 120 }))
          animateToStep('otp')
          break
        case 'user_blocked':
          userStore.setUserBlocked(login)
          updateValue({ loading: false })
          animateToStep('blocked')
          break
        default:
          updateValue({ error: t('loginPage.error.unexpected'), loading: false })
          break
      }
    } else {
      updateValue({ error: response.data?.message || t('loginPage.error.unexpected'), loading: false, password: '' })
    }
  }

  // --- Passkey login ---
  const [passkeySupported] = React.useState(() => isPasskeySupported())
  const [passkeyLoading, setPasskeyLoading] = React.useState(false)

  const handlePasskeyLogin = async (): Promise<void> => {
    if (values.loading || passkeyLoading) return
    setPasskeyLoading(true)
    updateValue({ error: '' })

    const result = await loginWithPasskey()
    switch (result.status) {
      case 'success':
        userStore.setUserData(result.data.user)
        await workspacesStore.fetch()
        navigateToDashboard()
        break
      case 'cancelled':
        setPasskeyLoading(false)
        break
      case 'error':
        setPasskeyLoading(false)
        updateValue({
          error:
            result.code === 'AUTH_PASSKEY_USER_BLOCKED'
              ? t('loginPage.passkey.error.blocked')
              : t('loginPage.passkey.error.failed'),
        })
        break
    }
  }

  // --- OTP handlers ---
  const otpFormRef = React.useRef<HTMLFormElement>(null)

  const handleOtpChange = (otp: string) => {
    setOtpValues(prev => ({ ...prev, otp, error: '' }))
  }

  const handleOtpComplete = () => {
    otpFormRef.current?.requestSubmit()
  }

  const handleOtpSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (otpValues.loading || !otpValues.otp) return

    setOtpValues(prev => ({ ...prev, loading: true, error: '' }))
    const response = await API.getInstance().authWithOtp(userStore.sessionId!, otpValues.otp)

    if (response.ok === true) {
      userStore.setUserData(response.data.user)
      await workspacesStore.fetch()
      navigateToDashboard()
    } else {
      setOtpValues(prev => ({
        ...prev,
        loading: false,
        error: response.data?.message || t('loginPage.error.invalidOtp'),
        otp: '',
      }))
    }
  }

  const handleResendOtp = async (): Promise<void> => {
    if (otpValues.resendCooldown > 0) return
    const response = await API.getInstance().login(values.login, values.password)
    if (response.ok && response.data.status === 'otp_required') {
      userStore.setOtpRequired(response.data.session_id, response.data.delivery_method, values.login)
      setOtpValues(prev => ({ ...prev, resendCooldown: 120, error: '' }))
    }
  }

  // --- Unblock handlers ---
  const handleUnblockSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (unblockValues.loading || !unblockValues.code) return

    setUnblockValues(prev => ({ ...prev, loading: true, error: '' }))
    const response = await API.getInstance().unblockUser(userStore.pendingEmail!, unblockValues.code)

    if (response.ok === true) {
      setUnblockValues(prev => ({ ...prev, loading: false, success: true }))
      setTimeout(() => handleBackToCredentials(), 2000)
    } else {
      setUnblockValues(prev => ({
        ...prev,
        loading: false,
        error: response.data?.message || t('loginPage.error.invalidUnblockCode'),
        code: '',
      }))
    }
  }

  const handleBackToCredentials = () => {
    userStore.resetAuthFlow()
    setOtpValues({ otp: '', error: '', loading: false, resendCooldown: 0 })
    setUnblockValues({ code: '', error: '', loading: false, success: false })
    animateToStep('credentials')
  }

  const [title] = React.useState(getRandomArrayItem(TITLES))

  // --- Render sub-forms ---

  const renderCredentialsForm = () => (
    <form noValidate autoComplete="off" onSubmit={handleLogin}
      className="flex flex-col justify-center w-[calc(100%-30px)] max-w-[400px]"
    >
      <p className="mb-4 text-sm text-gray-500 text-center">
        {t('loginPage.loginPrompt')}
      </p>

      <TextInput
        label={t('loginPage.email')}
        type="email"
        value={values.login}
        onChange={handleChange('login')}
        disabled={values.loading}
        error={Boolean(values.loginError || values.error)}
        helperText={values.loginError}
      />

      <TextInput
        label={t('loginPage.password')}
        type={values.showPassword ? 'text' : 'password'}
        value={values.password}
        onChange={handleChange('password')}
        disabled={values.loading}
        error={Boolean(values.passwordError || values.error)}
        helperText={values.passwordError || values.error}
        endAdornment={
          <button
            type="button"
            className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            onClick={() => updateValue({ showPassword: !values.showPassword })}
            onMouseDown={e => e.preventDefault()}
            aria-label="toggle password visibility"
          >
            {values.showPassword
              ? <EyeIcon className="w-5 h-5" />
              : <EyeSlashIcon className="w-5 h-5" />
            }
          </button>
        }
      />

      <button
        type="submit"
        disabled={values.loading}
        className="mt-6 w-full py-3 px-6 bg-brand text-white text-sm font-medium rounded-md
          hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors touch-manipulation"
      >
        {t('loginPage.logIn')}
      </button>

      {passkeySupported && (
        <>
          <div className="mt-6 flex items-center gap-3" aria-hidden="true">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs text-gray-400">{t('loginPage.passkey.or')}</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
          <button
            type="button"
            onClick={handlePasskeyLogin}
            disabled={values.loading || passkeyLoading}
            className="mt-4 w-full py-3 px-6 flex items-center justify-center gap-2
              border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-md
              hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors touch-manipulation"
          >
            <FingerPrintIcon className="w-5 h-5" />
            {t('loginPage.passkey.signIn')}
          </button>
        </>
      )}
    </form>
  )

  const renderOtpForm = () => (
    <form ref={otpFormRef} noValidate autoComplete="off" onSubmit={handleOtpSubmit}
      className="flex flex-col justify-center w-[calc(100%-30px)] max-w-[400px]"
    >
      <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
        <LockClosedIcon className="w-16 h-16 text-brand" />
      </div>

      <h2 className="text-xl font-semibold text-center mb-3">
        {t('loginPage.otp.title')}
      </h2>

      <p className="text-sm text-gray-500 text-center">
        {userStore.deliveryMethod === 'email'
          ? t('loginPage.otp.sentToEmail')
          : t('loginPage.otp.useAuthApp')}
      </p>

      {userStore.deliveryMethod === 'email' && userStore.pendingEmail && (
        <p className="mt-1 text-sm font-medium text-brand text-center">
          {userStore.pendingEmail}
        </p>
      )}

      <PinInput
        value={otpValues.otp}
        onChange={handleOtpChange}
        onComplete={handleOtpComplete}
        disabled={otpValues.loading}
        autoFocus
        className="my-6"
      />

      {otpValues.error && (
        <p className="mb-4 text-sm text-red-500 text-center">{otpValues.error}</p>
      )}

      <button
        type="submit"
        disabled={otpValues.loading || otpValues.otp.replace(/\s/g, '').length !== 6}
        className="mt-2 w-full py-3 px-6 bg-brand text-white text-sm font-medium rounded-md
          hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors touch-manipulation"
      >
        {t('loginPage.otp.verify')}
      </button>

      {userStore.deliveryMethod === 'email' && (
        <p className="mt-6 text-sm text-gray-500 text-center">
          {otpValues.resendCooldown > 0 ? (
            t('loginPage.otp.resendIn', { seconds: otpValues.resendCooldown })
          ) : (
            <button
              type="button"
              onClick={handleResendOtp}
              className="text-brand hover:underline text-sm font-medium"
            >
              {t('loginPage.otp.resend')}
            </button>
          )}
        </p>
      )}
    </form>
  )

  const renderBlockedForm = () => (
    <form noValidate autoComplete="off" onSubmit={handleUnblockSubmit}
      className="flex flex-col justify-center w-[calc(100%-30px)] max-w-[400px]"
    >
      <h2 className="text-lg font-semibold text-red-600 text-center mb-2">
        {t('loginPage.blocked.title')}
      </h2>

      {unblockValues.success ? (
        <p className="mb-4 text-sm text-green-600 text-center">
          {t('loginPage.blocked.success')}
        </p>
      ) : (
        <>
          <p className="mb-4 text-sm text-gray-600 text-center">
            {t('loginPage.blocked.description')}
          </p>

          <TextInput
            label={t('loginPage.blocked.codeLabel')}
            type="text"
            value={unblockValues.code}
            onChange={e => setUnblockValues(prev => ({ ...prev, code: e.target.value, error: '' }))}
            disabled={unblockValues.loading}
            error={Boolean(unblockValues.error)}
            helperText={unblockValues.error}
            autoFocus
          />

          <button
            type="submit"
            disabled={unblockValues.loading || !unblockValues.code}
            className="mt-6 w-full py-3 px-6 bg-brand text-white text-sm font-medium rounded-md
              hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors touch-manipulation"
          >
            {t('loginPage.blocked.unblock')}
          </button>
        </>
      )}
    </form>
  )

  const renderForm = () => {
    const form = (() => {
      switch (displayedStep) {
        case 'otp': return renderOtpForm()
        case 'blocked': return renderBlockedForm()
        default: return renderCredentialsForm()
      }
    })()

    return (
      <div className={`flex flex-col items-center w-full ${animationClass}`}>
        {form}
      </div>
    )
  }

  return (
    <div className="relative flex w-full min-h-dvh bg-white">
      {/* Content panel */}
      <div className="relative flex flex-col items-center flex-1 min-h-dvh
        md:flex-none md:w-[40%] md:min-w-[560px]"
      >
        <LogoWithText className="absolute top-12" />

        {step !== 'credentials' && (
          <button
            type="button"
            onClick={handleBackToCredentials}
            className="absolute top-[120px] left-6 flex items-center gap-2 px-4 py-2
              bg-transparent border-none text-sm text-gray-600 rounded-lg
              hover:bg-gray-100 transition-colors cursor-pointer touch-manipulation"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            {t('loginPage.backToLogin')}
          </button>
        )}

        {/* Spacer: pushes content below the logo area */}
        <div className="flex-1 min-h-[100px]" />

        {displayedStep === 'credentials' && (
          <h1 className={`mb-2 text-5xl md:text-7xl font-extralight tracking-tight text-center ${animationClass}`}>{title}</h1>
        )}

        {renderForm()}

        {/* Spacer: balances bottom to center content between logo and edge */}
        <div className="flex-1 min-h-[40px]" />
      </div>

      {/* Cover image (hidden on mobile) */}
      <div
        className="hidden md:flex flex-1 bg-cover bg-center bg-no-repeat bg-gray-100"
        style={{ backgroundImage: `url(${API_URL}/cover)` }}
      />
    </div>
  )
})

export default inject(WORKSPACES_STORE)(inject(USER_STORE)(LoginPage))
